/**
 * ReviewQueue.tsx — Human review of ambiguous match candidates.
 *
 * Rebuilt in final pass.
 *
 * Two modes:
 *   - Individual: side-by-side record comparison cards (Client record vs
 *     Meridian registry OR another client record) with approve/reject
 *   - Batch: clusters grouped by mismatch type; expand to select/unselect
 *     individual items; approve or reject whole cluster (or just the selected)
 *
 * Features preserved:
 *   - Pagination (10 per page in individual mode)
 *   - Search (name / city / client)
 *   - Propagation banner: N items auto-resolvable based on past decisions
 *     → one-click "Run propagation" endpoint
 *   - Cluster item pagination (load-more, 10 per page)
 *   - Posthog analytics capture on decisions
 *
 * Changes:
 *   - Uses shared UI (Card, Button, Tag, Input, Icon, Pagination, toast)
 *   - Success / error messages now use toast instead of inline banners
 *   - approve/reject buttons are Button variant="secondary" with a semantic
 *     border color applied via style override (Button doesn't have a
 *     'success' variant natively)
 */

import { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import { captureEvent } from '../../analytics/posthog';
import {
  Card,
  Button,
  Tag, type TagKind,
  Icon,
  Pagination,
  EmptyState,
  Skeleton,
  Input,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface ReviewItem {
  id: string;
  name_a: string;
  address_a: string;
  city_a: string;
  vat_a: string;
  client_a: string;
  name_b: string;
  address_b: string;
  city_b: string;
  vat_b: string;
  client_b: string;
  confidence_score: number;
  reason: string;
  candidate_census_id?: string;
  match_type?: 'hub' | 'pairwise';
}

interface QueueResponse {
  items: ReviewItem[];
  total: number;
  page: number;
  pages: number;
}

interface ClusterItem {
  cluster_id: string;
  label: string;
  mismatch_type: string;
  reason: string;
  vat: string;
  confidence_band: string;
  confidence_range: string;
  item_count: number;
}

interface PropagationSummary {
  propagatable_count: number;
  pattern_count: number;
}

/* ═══════════════ HELPERS ═══════════════ */

function confKind(score: number): TagKind {
  if (score >= 0.9) return 'success';
  if (score >= 0.65) return 'warning';
  return 'danger';
}

function confColorValue(score: number): string {
  if (score >= 0.9) return theme.semantic.success;
  if (score >= 0.65) return theme.semantic.warning;
  return theme.semantic.danger;
}

function reasonLabel(reason: string): string {
  const map: Record<string, string> = {
    'fuzzy_name_address': 'Smart match — Name & Address',
    'fuzzy_name_only':    'Smart match — Name',
    'geo_proximity_only': 'GPS proximity',
  };
  return map[reason] || reason.replace(/_/g, ' ');
}

const LIMIT = 10;

/* ═══════════════ PAGE ═══════════════ */

export default function ReviewQueue() {
  const [mode, setMode] = useState<'individual' | 'batch'>('individual');
  const [data, setData] = useState<QueueResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { maskClientName, modeVersion } = useAdmin();

  // Batch mode state
  const [clusters, setClusters] = useState<ClusterItem[]>([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
  const [clusterDeciding, setClusterDeciding] = useState<string | null>(null);
  const [clusterItems, setClusterItems] = useState<Record<string, ReviewItem[]>>({});
  const [clusterItemsPage, setClusterItemsPage] = useState<Record<string, number>>({});
  const [clusterItemsTotal, setClusterItemsTotal] = useState<Record<string, number>>({});
  const [selectedItems, setSelectedItems] = useState<Record<string, Set<string>>>({});
  const [loadingItems, setLoadingItems] = useState<string | null>(null);

  // Propagation
  const [propagation, setPropagation] = useState<PropagationSummary | null>(null);
  const [propagating, setPropagating] = useState(false);

  useEffect(() => {
    document.title = 'Review Queue | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const fetchPage = useCallback(async (p: number, q: string = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (q) params.set('search', q);
      const res = await api.get<QueueResponse>(`/api/review/queue?${params}`);
      setData(res.data);
      setPage(res.data.page);
    } catch {
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClusters = useCallback(async () => {
    setClustersLoading(true);
    try {
      const res = await api.get<ClusterItem[]>('/api/review/clusters');
      setClusters(res.data);
    } catch {
      toast.error('Failed to load clusters');
    } finally {
      setClustersLoading(false);
    }
  }, []);

  const fetchPropagation = useCallback(async () => {
    try {
      const res = await api.get<PropagationSummary>('/api/review/propagation');
      setPropagation(res.data);
    } catch {
      // silent — propagation is optional
    }
  }, []);

  const loadClusterItems = useCallback(async (clusterId: string, p: number = 1) => {
    setLoadingItems(clusterId);
    try {
      const res = await api.get<{ items: ReviewItem[]; total: number; pages: number }>(
        `/api/review/clusters/${clusterId}?page=${p}&limit=10`
      );
      setClusterItems(prev => ({
        ...prev,
        [clusterId]: p === 1 ? res.data.items : [...(prev[clusterId] || []), ...res.data.items],
      }));
      setClusterItemsTotal(prev => ({ ...prev, [clusterId]: res.data.total }));
      setClusterItemsPage(prev => ({ ...prev, [clusterId]: p }));
      // Default: select all loaded items
      if (p === 1) {
        setSelectedItems(prev => ({
          ...prev,
          [clusterId]: new Set(res.data.items.map(it => it.id)),
        }));
      } else {
        setSelectedItems(prev => ({
          ...prev,
          [clusterId]: new Set([
            ...Array.from(prev[clusterId] || []),
            ...res.data.items.map(it => it.id),
          ]),
        }));
      }
    } catch {
      // silent — caller sees empty list
    } finally {
      setLoadingItems(null);
    }
  }, []);

  useEffect(() => { fetchPage(1, search); fetchPropagation(); }, [fetchPage, fetchPropagation, search, modeVersion]);

  useEffect(() => {
    if (mode === 'batch') fetchClusters();
  }, [mode, fetchClusters, modeVersion]);

  const handleSearch = () => { setSearch(searchInput.trim()); setPage(1); };
  const clearSearch = () => { setSearchInput(''); setSearch(''); setPage(1); };

  /* ─── Decision actions ─── */

  const handleDecision = async (id: string, decision: 'approve' | 'reject') => {
    setDeciding(id);
    try {
      await api.post('/api/review/decide', { id, decision });
      captureEvent('review_decision', { decision, id });
      await fetchPage(page, search);
      fetchPropagation();
      toast.success(decision === 'approve' ? 'Match approved' : 'Match rejected');
    } catch {
      toast.error('Decision failed');
    } finally {
      setDeciding(null);
    }
  };

  const handleClusterDecision = async (clusterId: string, decision: 'approve' | 'reject') => {
    setClusterDeciding(clusterId);
    try {
      const selected = selectedItems[clusterId];
      const itemIds = selected && selected.size > 0 ? Array.from(selected) : undefined;
      const res = await api.post(`/api/review/clusters/${clusterId}/decide`, {
        decision,
        item_ids: itemIds,
      });
      const resolved = res.data.resolved || 0;
      toast.success(`${decision === 'approve' ? 'Approved' : 'Rejected'} ${resolved} items`);
      setClusterItems(prev => { const n = { ...prev }; delete n[clusterId]; return n; });
      setSelectedItems(prev => { const n = { ...prev }; delete n[clusterId]; return n; });
      setExpandedCluster(null);
      await fetchClusters();
      await fetchPage(1, search);
      fetchPropagation();
    } catch {
      toast.error('Cluster decision failed');
    } finally {
      setClusterDeciding(null);
    }
  };

  const handleRunPropagation = async () => {
    setPropagating(true);
    try {
      const res = await api.post('/api/review/propagation/run', { dry_run: false });
      const resolved = res.data.resolved || 0;
      toast.success(`Propagation resolved ${resolved} items based on prior decisions`);
      await fetchPage(1, search);
      if (mode === 'batch') await fetchClusters();
      fetchPropagation();
    } catch {
      toast.error('Propagation failed');
    } finally {
      setPropagating(false);
    }
  };

  const toggleItem = (clusterId: string, itemId: string) => {
    setSelectedItems(prev => {
      const current = new Set(prev[clusterId] || []);
      if (current.has(itemId)) current.delete(itemId);
      else current.add(itemId);
      return { ...prev, [clusterId]: current };
    });
  };

  const toggleAll = (clusterId: string) => {
    const items = clusterItems[clusterId] || [];
    const current = selectedItems[clusterId] || new Set();
    const allSelected = items.every(it => current.has(it.id));
    setSelectedItems(prev => ({
      ...prev,
      [clusterId]: allSelected ? new Set() : new Set(items.map(it => it.id)),
    }));
  };

  /* ─── Loading ─── */
  if (loading && !data) {
    return (
      <div style={{ padding: 32 }}>
        <Skeleton width={200} height={28} />
        <div style={{ marginTop: 12 }}><Skeleton width={300} height={14} /></div>
        {[0, 1, 2, 3].map(i => (
          <Card key={i} style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 24 }}>
              <div style={{ flex: 1 }}>
                <Skeleton width="40%" height={12} />
                <div style={{ marginTop: 12 }}><Skeleton width="70%" height={14} /></div>
                <div style={{ marginTop: 8 }}><Skeleton width="60%" height={14} /></div>
              </div>
              <div style={{ width: 1, background: theme.borderColor.default }} />
              <div style={{ flex: 1 }}>
                <Skeleton width="40%" height={12} />
                <div style={{ marginTop: 12 }}><Skeleton width="70%" height={14} /></div>
                <div style={{ marginTop: 8 }}><Skeleton width="60%" height={14} /></div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const items = data?.items ?? [];
  const startIdx = (page - 1) * LIMIT + 1;
  const endIdx = Math.min(page * LIMIT, total);

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary, margin: 0,
          letterSpacing: '-0.02em',
        }}>Review queue</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Approve or reject ambiguous match candidates</div>
      </div>

      {/* Propagation banner */}
      {propagation && propagation.propagatable_count > 0 && (
        <div style={{
          padding: '14px 18px',
          background: theme.semantic.successSoft,
          border: `1px solid ${theme.semantic.successBorder}`,
          borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16,
          marginBottom: 18,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: theme.text.primary,
            fontSize: 14,
          }}>
            <Icon.Sparkles size={16} />
            <span>
              <strong style={{
                color: theme.semantic.success,
                fontWeight: 700,
              }}>{propagation.propagatable_count}</strong>
              {' '}items could be auto-resolved based on prior decisions
              <span style={{
                color: theme.text.tertiary,
                marginLeft: 8,
                fontSize: 13,
              }}>
                ({propagation.pattern_count} patterns detected)
              </span>
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            loading={propagating}
            onClick={handleRunPropagation}
            leftIcon={<Icon.Zap size={12} />}
            style={{
              borderColor: theme.semantic.successBorder,
              color: theme.semantic.success,
            }}
          >{propagating ? 'Running…' : 'Run propagation'}</Button>
        </div>
      )}

      {/* Mode toggle */}
      <div style={{
        display: 'inline-flex',
        gap: 4,
        padding: 4,
        background: theme.surface.panel,
        border: `1px solid ${theme.borderColor.default}`,
        borderRadius: 10,
        marginBottom: 20,
      }}>
        {(['individual', 'batch'] as const).map(m => {
          const active = mode === m;
          return (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{
                padding: '7px 16px',
                borderRadius: 7,
                fontFamily: theme.font.ui,
                fontWeight: active ? 600 : 500,
                fontSize: 13,
                background: active ? theme.surface.card : 'transparent',
                border: 'none',
                color: active ? theme.text.primary : theme.text.secondary,
                cursor: 'pointer',
                transition: 'all 120ms ease',
              }}
            >{m === 'individual' ? 'Individual review' : 'Batch review'}</button>
          );
        })}
      </div>

      {/* ═══════ INDIVIDUAL MODE ═══════ */}
      {mode === 'individual' && (
        <>
          {/* Search bar */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            marginBottom: 16,
            maxWidth: 560,
          }}>
            <div style={{ flex: 1 }}>
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name, city, or client…"
                leftIcon={<Icon.Search size={14} />}
              />
            </div>
            <Button variant="secondary" size="md" onClick={handleSearch}>Search</Button>
            {search && (
              <Button variant="ghost" size="md" onClick={clearSearch}>Clear</Button>
            )}
          </div>

          {/* Status row */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 16,
          }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 8,
            }}>
              <span style={{
                color: theme.semantic.warning,
                fontWeight: 700,
                fontSize: 22,
                fontFamily: theme.font.data,
                fontVariantNumeric: 'tabular-nums',
              }}>{total.toLocaleString()}</span>
              <span style={{
                color: theme.text.secondary,
                fontSize: 13,
              }}>items pending review</span>
            </div>
            {total > 0 && (
              <div style={{
                color: theme.text.tertiary,
                fontSize: 12,
                fontFamily: theme.font.data,
              }}>
                Showing {startIdx}–{endIdx} of {total} · Page {page} of {pages}
              </div>
            )}
          </div>

          {/* Pagination (top) */}
          {pages > 1 && (
            <div style={{ marginBottom: 18, display: 'flex' }}>
              <Pagination page={page} totalPages={pages} onChange={p => fetchPage(p, search)} />
            </div>
          )}

          {/* Cards */}
          {items.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Icon.Check size={28} />}
                title="All clear"
                description={search
                  ? `No pending items match "${search}".`
                  : 'No items pending review.'}
              />
            </Card>
          ) : (
            items.map(item => (
              <ReviewCard
                key={item.id}
                item={item}
                deciding={deciding}
                onDecision={handleDecision}
                maskClientName={maskClientName}
              />
            ))
          )}
        </>
      )}

      {/* ═══════ BATCH MODE ═══════ */}
      {mode === 'batch' && (
        <>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            marginBottom: 16,
          }}>
            <span style={{
              color: theme.semantic.warning,
              fontWeight: 700,
              fontSize: 22,
              fontFamily: theme.font.data,
              fontVariantNumeric: 'tabular-nums',
            }}>{clusters.length}</span>
            <span style={{
              color: theme.text.secondary,
              fontSize: 13,
            }}>clusters ({total.toLocaleString()} total items)</span>
          </div>

          {clustersLoading && (
            <Card><div style={{ color: theme.text.secondary }}>Loading clusters…</div></Card>
          )}

          {!clustersLoading && clusters.length === 0 && (
            <Card>
              <EmptyState
                icon={<Icon.Check size={28} />}
                title="No clusters found"
                description="Pending items are evaluated and grouped when the pipeline completes."
              />
            </Card>
          )}

          {clusters.map(cluster => {
            const expanded = expandedCluster === cluster.cluster_id;
            const items = clusterItems[cluster.cluster_id] || [];
            const clusterTotal = clusterItemsTotal[cluster.cluster_id] || cluster.item_count;
            const selected = selectedItems[cluster.cluster_id] || new Set<string>();
            const selectedCount = selected.size;
            const hasMore = items.length < clusterTotal;
            const currentPage = clusterItemsPage[cluster.cluster_id] || 1;

            return (
              <div
                key={cluster.cluster_id}
                style={{
                  background: theme.surface.panel,
                  border: `1px solid ${theme.borderColor.default}`,
                  borderLeft: `3px solid ${theme.semantic.warning}`,
                  borderRadius: 10,
                  padding: 18,
                  marginBottom: 14,
                  opacity: clusterDeciding === cluster.cluster_id ? 0.55 : 1,
                  transition: 'opacity 200ms ease',
                }}>
                {/* Cluster header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  gap: 16, flexWrap: 'wrap',
                }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700,
                      color: theme.semantic.warning,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      marginBottom: 4,
                    }}>{cluster.mismatch_type}</div>
                    <div style={{
                      display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                    }}>
                      <span style={{
                        fontFamily: theme.font.data,
                        color: theme.brand.cyanLight,
                        fontWeight: 700,
                        fontSize: 20,
                      }}>{cluster.item_count}</span>
                      <span style={{
                        fontSize: 13, color: theme.text.secondary,
                      }}>items</span>
                      <Tag kind="neutral" size="sm">{cluster.confidence_range} confidence</Tag>
                      {cluster.vat && (
                        <Tag kind="neutral" size="sm" mono>Tax ID {cluster.vat}</Tag>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={clusterDeciding === cluster.cluster_id}
                      disabled={clusterDeciding === cluster.cluster_id || (expanded && selectedCount === 0)}
                      onClick={() => handleClusterDecision(cluster.cluster_id, 'approve')}
                      style={{
                        borderColor: theme.semantic.successBorder,
                        color: theme.semantic.success,
                      }}
                    >
                      {expanded ? `Approve selected (${selectedCount})` : 'Approve all'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={clusterDeciding === cluster.cluster_id}
                      disabled={clusterDeciding === cluster.cluster_id || (expanded && selectedCount === 0)}
                      onClick={() => handleClusterDecision(cluster.cluster_id, 'reject')}
                      style={{
                        borderColor: theme.semantic.dangerBorder,
                        color: theme.semantic.danger,
                      }}
                    >
                      {expanded ? `Reject selected (${selectedCount})` : 'Reject all'}
                    </Button>
                  </div>
                </div>

                {/* Expand toggle */}
                <button
                  onClick={() => {
                    if (expanded) {
                      setExpandedCluster(null);
                    } else {
                      setExpandedCluster(cluster.cluster_id);
                      if (!clusterItems[cluster.cluster_id]) {
                        loadClusterItems(cluster.cluster_id, 1);
                      }
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: theme.brand.cyanLight,
                    fontSize: 12, fontWeight: 500,
                    fontFamily: theme.font.ui,
                    cursor: 'pointer',
                    marginTop: 10, padding: 0,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                >
                  {expanded ? 'Hide items' : `Show all ${cluster.item_count} items`}
                  {expanded
                    ? <Icon.ChevronUp size={12} />
                    : <Icon.ChevronDown size={12} />}
                </button>

                {/* Expanded items */}
                {expanded && (
                  <div style={{
                    marginTop: 14,
                    borderTop: `1px solid ${theme.borderColor.subtle}`,
                    paddingTop: 14,
                  }}>
                    {/* Select all */}
                    {items.length > 0 && (
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        cursor: 'pointer',
                        marginBottom: 8,
                        fontSize: 12,
                        color: theme.text.secondary,
                      }}>
                        <input
                          type="checkbox"
                          checked={items.length > 0 && items.every(it => selected.has(it.id))}
                          onChange={() => toggleAll(cluster.cluster_id)}
                          style={{ accentColor: theme.brand.cyan }}
                        />
                        Select all ({items.length} loaded of {clusterTotal})
                      </label>
                    )}

                    {items.map((item, i) => (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 14,
                          padding: '8px 0',
                          borderBottom: i < items.length - 1
                            ? `1px solid ${theme.borderColor.subtle}`
                            : 'none',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}>
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          onChange={() => toggleItem(cluster.cluster_id, item.id)}
                          style={{
                            accentColor: theme.brand.cyan,
                            flexShrink: 0,
                          }}
                        />
                        <ClusterItemSide label="A" name={item.name_a} city={item.city_a} />
                        <ClusterItemSide label="B" name={item.name_b} city={item.city_b} />
                        <div style={{
                          fontFamily: theme.font.data,
                          color: confColorValue(item.confidence_score),
                          fontWeight: 700,
                          minWidth: 40,
                          textAlign: 'right',
                          fontSize: 12,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {Math.round(item.confidence_score * 100)}%
                        </div>
                      </label>
                    ))}

                    {/* Load more */}
                    {hasMore && (
                      <div style={{ marginTop: 12 }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          fullWidth
                          loading={loadingItems === cluster.cluster_id}
                          onClick={() => loadClusterItems(cluster.cluster_id, currentPage + 1)}
                        >
                          {loadingItems === cluster.cluster_id
                            ? 'Loading…'
                            : `Load more (${items.length} of ${clusterTotal})`}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

/* ─── Individual review card ─── */

function ReviewCard({
  item, deciding, onDecision, maskClientName,
}: {
  item: ReviewItem;
  deciding: string | null;
  onDecision: (id: string, decision: 'approve' | 'reject') => void;
  maskClientName: (name: string, index?: number) => string;
}) {
  const isBusy = deciding === item.id;
  const borderColor = confColorValue(item.confidence_score);

  return (
    <div
      style={{
        background: theme.surface.panel,
        border: `1px solid ${theme.borderColor.default}`,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 10,
        padding: 20,
        marginBottom: 14,
        opacity: isBusy ? 0.55 : 1,
        transition: 'opacity 200ms ease',
      }}
      className="meridian-review-card"
    >
      <style>{`
        @media (min-width: 900px) {
          .meridian-review-card .compare-grid {
            display: grid !important;
            grid-template-columns: 1fr 1px 1fr !important;
            gap: 20px !important;
          }
        }
      `}</style>

      <div className="compare-grid" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* A — Client record */}
        <div>
          <div style={sectionHeaderStyle}>
            Client record — {maskClientName(item.client_a || '?')}
          </div>
          <RecordField label="Name" value={item.name_a} />
          <RecordField label="Address" value={item.address_a} />
          <RecordField label="City" value={item.city_a} />
          <RecordField label="Tax ID" value={item.vat_a} />
        </div>

        {/* Divider (desktop) */}
        <div style={{
          background: theme.borderColor.default,
          width: '100%',
          height: 1,
        }} />

        {/* B — Registry or other client */}
        <div>
          <div style={sectionHeaderStyle}>
            {item.match_type === 'hub'
              ? 'Meridian registry'
              : `Record B — ${maskClientName(item.client_b || '?')}`}
          </div>
          {item.match_type === 'hub' && item.candidate_census_id && (
            <div style={{ marginTop: 6 }}>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: theme.text.tertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>Census ID</div>
              <div style={{
                color: theme.brand.cyanLight,
                fontSize: 14,
                fontFamily: theme.font.data,
                marginTop: 2,
              }}>{item.candidate_census_id}</div>
            </div>
          )}
          <RecordField label="Name" value={item.name_b} />
          <RecordField label="Address" value={item.address_b} />
          <RecordField label="City" value={item.city_b} />
          {item.match_type !== 'hub' && <RecordField label="Tax ID" value={item.vat_b} />}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 18,
        paddingTop: 14,
        borderTop: `1px solid ${theme.borderColor.subtle}`,
        gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            variant="secondary"
            loading={isBusy}
            disabled={isBusy}
            onClick={() => onDecision(item.id, 'approve')}
            style={{
              borderColor: theme.semantic.successBorder,
              color: theme.semantic.success,
            }}
            leftIcon={<Icon.Check size={14} />}
          >Approve</Button>
          <Button
            variant="secondary"
            loading={isBusy}
            disabled={isBusy}
            onClick={() => onDecision(item.id, 'reject')}
            style={{
              borderColor: theme.semantic.dangerBorder,
              color: theme.semantic.danger,
            }}
            leftIcon={<Icon.X size={14} />}
          >Reject</Button>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            <Tag kind={confKind(item.confidence_score)} size="sm" mono>
              {Math.round(item.confidence_score * 100)}%
            </Tag>
            <span style={{
              color: theme.text.tertiary,
              fontSize: 12,
            }}>{reasonLabel(item.reason)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecordField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{
        fontSize: 11, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}>{label}</div>
      <div style={{
        color: value ? theme.text.primary : theme.text.tertiary,
        fontSize: 14,
        marginTop: 2,
      }}>{value || '—'}</div>
    </div>
  );
}

function ClusterItemSide({ label, name, city }: { label: string; name: string; city: string }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <span style={{
        fontSize: 10, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginRight: 8,
      }}>{label}:</span>
      <span style={{ color: theme.text.primary }}>{name || '—'}</span>
      <span style={{ color: theme.text.tertiary, marginLeft: 6 }}>{city}</span>
    </div>
  );
}

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600,
  color: theme.text.tertiary,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  marginBottom: 4,
};
